import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { fetchMaintenanceMode, setMaintenanceModeBackend } from '../thunks';

interface UIState {
  isSidebarOpen: boolean;
  isCartOpen: boolean;
  isMobileMenuOpen: boolean;
  isSearchOpen: boolean;
  isAuthModalOpen: boolean;
  authModalMessage?: string;
  pendingAction?: 'cart' | 'wishlist' | 'checkout' | null;
  theme: 'light' | 'dark';
  notifications: Notification[];
  loading: {
    global: boolean;
    [key: string]: boolean;
  };
  isMaintenanceMode: boolean;
}

interface Notification {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  title: string;
  message: string;
  duration?: number;
  timestamp: number;
}

const initialState: UIState = {
  isSidebarOpen: false,
  isCartOpen: false,
  isMobileMenuOpen: false,
  isSearchOpen: false,
  isAuthModalOpen: false,
  authModalMessage: undefined,
  pendingAction: null,
  theme: 'light',
  notifications: [],
  loading: {
    global: false,
  },
  isMaintenanceMode: false, // Loaded from backend on app init
};

const uiSlice = createSlice({
  name: 'ui',
  initialState,
  reducers: {
    openAuthModal: (state, action: PayloadAction<{ message?: string; pendingAction?: 'cart' | 'wishlist' | 'checkout' } | undefined>) => {
      state.isAuthModalOpen = true;
      state.authModalMessage = action.payload?.message || 'Please sign in to continue';
      state.pendingAction = action.payload?.pendingAction || null;
    },
    closeAuthModal: (state) => {
      state.isAuthModalOpen = false;
      state.authModalMessage = undefined;
      state.pendingAction = null;
    },
    toggleSidebar: (state) => {
      state.isSidebarOpen = !state.isSidebarOpen;
    },
    closeSidebar: (state) => {
      state.isSidebarOpen = false;
    },
    toggleCart: (state) => {
      state.isCartOpen = !state.isCartOpen;
    },
    closeCart: (state) => {
      state.isCartOpen = false;
    },
    toggleMobileMenu: (state) => {
      state.isMobileMenuOpen = !state.isMobileMenuOpen;
    },
    closeMobileMenu: (state) => {
      state.isMobileMenuOpen = false;
    },
    toggleSearch: (state) => {
      state.isSearchOpen = !state.isSearchOpen;
    },
    closeSearch: (state) => {
      state.isSearchOpen = false;
    },
    setTheme: (state, action: PayloadAction<'light' | 'dark'>) => {
      state.theme = action.payload;
    },
    addNotification: (state, action: PayloadAction<Omit<Notification, 'id' | 'timestamp'>>) => {
      const notification: Notification = {
        ...action.payload,
        id: Date.now().toString(),
        timestamp: Date.now(),
      };
      state.notifications.push(notification);
    },
    removeNotification: (state, action: PayloadAction<string>) => {
      state.notifications = state.notifications.filter(
        notification => notification.id !== action.payload
      );
    },
    clearNotifications: (state) => {
      state.notifications = [];
    },
    setLoading: (state, action: PayloadAction<{ key: string; value: boolean }>) => {
      state.loading[action.payload.key] = action.payload.value;
    },
    setGlobalLoading: (state, action: PayloadAction<boolean>) => {
      state.loading.global = action.payload;
    },
    setMaintenanceMode: (state, action: PayloadAction<boolean>) => {
      state.isMaintenanceMode = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMaintenanceMode.fulfilled, (state, action) => {
        state.isMaintenanceMode = action.payload as boolean;
      })
      .addCase(setMaintenanceModeBackend.fulfilled, (state, action) => {
        state.isMaintenanceMode = action.payload as boolean;
      });
  },
});

export const {
  openAuthModal,
  closeAuthModal,
  toggleSidebar,
  closeSidebar,
  toggleCart,
  closeCart,
  toggleMobileMenu,
  closeMobileMenu,
  toggleSearch,
  closeSearch,
  setTheme,
  addNotification,
  removeNotification,
  clearNotifications,
  setLoading,
  setGlobalLoading,
  setMaintenanceMode,
} = uiSlice.actions;

export default uiSlice.reducer;
